import weatherAnim from 'Assets/lottie/weather_anim.json'
import weatherCloudy from 'Assets/lottie/weather_cloudy.json'
import weatherDrizzle from 'Assets/lottie/weather_drizzle.json'
import weatherFog from 'Assets/lottie/weather_fog.json'
import weatherHail from 'Assets/lottie/weather_hail.json'
import weatherOvercast from 'Assets/lottie/weather_overcast.json'
import weatherPartly from 'Assets/lottie/weather_partly.json'
import weatherPartiallyShower from 'Assets/lottie/weather_partially_shower.json'
import weatherRainy from 'Assets/lottie/weather_rainy.json'
import weatherSnow from 'Assets/lottie/weather_snow.json'
import weatherSnowSunny from 'Assets/lottie/weather_snow_sunny.json'
import weatherStorm from 'Assets/lottie/weather_storm.json'
import weatherSunny from 'Assets/lottie/weather_sunny.json'
import weatherThunder from 'Assets/lottie/weather_thunder.json'
import weatherWind from 'Assets/lottie/weather_wind.json'
import clearDay from 'Assets/lottie/1.clear_day.json'
import clearNight from 'Assets/lottie/2.clear_night.json'
import partlyCloudyDay from 'Assets/lottie/3.partly_cloudy_day.json'
import partlyCloudyNight from 'Assets/lottie/4.partly_cloudy_night.json'
import cloudy from 'Assets/lottie/5.cloudy.json'
import rainyDay from 'Assets/lottie/6.rainy_day.json'
import rainyNight from 'Assets/lottie/7.rainy_day.json'
import heavyRain from 'Assets/lottie/8.heavy_rain.json'
import thunderstormDay from 'Assets/lottie/9.thunderstorm_day.json'
import thunderstormNight from 'Assets/lottie/10.thunderstorm_night.json'
import snowDay from 'Assets/lottie/11.snow_day.json'
import snowNight from 'Assets/lottie/12.snow_night.json'
import fog from 'Assets/lottie/13.fog.json'
import drizzle from 'Assets/lottie/14.drizzle.json'
import wind from 'Assets/lottie/15.wind.json'
import mist from 'Assets/lottie/16.mist.json'
import clearNightIcon from 'Assets/lottie/weather_clear_night.json'
import partlyNightIcon from 'Assets/lottie/weather_cloudy_night.json'

const isDaytime = (dateTime) => {
  const time = dateTime || new Date()
  const hour = time.getHours()

  return hour >= 5 && hour < 19 // Dawn, Day, or Dusk
}

export const WEATHER_METHODS = {
  timeMethods: {
    getUtcTime(utcTime) {
      const date = new Date(utcTime)

      let hours = date.getUTCHours()
      const minutes = String(date.getUTCMinutes()).padStart(2, '0')
      const ampm = hours >= 12 ? 'PM' : 'AM'

      hours = hours % 12 || 12

      const time12hr = `${hours}:${minutes} ${ampm}`

      return time12hr
    },

    getDayOfWeek(utcTime) {
      const inputDate = new Date(utcTime)
      const today = new Date()

      // Normalize both dates to midnight
      inputDate.setHours(0, 0, 0, 0)
      today.setHours(0, 0, 0, 0)

      if (inputDate.getTime() === today.getTime()) {
        return 'Today'
      }

      return inputDate.toLocaleDateString(undefined, { weekday: 'short' })
    },

    getHourlyTime(forecastTime) {
      const inputDate = new Date(forecastTime)
      let hours = inputDate.getUTCHours()
      const ampm = hours >= 12 ? 'PM' : 'AM'

      hours = hours % 12 || 12

      return `${hours} ${ampm}`
    },
    getCurrentDateForTimezone(timezone) {
      const defaultOptions = {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      }

      return new Date().toLocaleDateString('en-Gb', {
        timeZone: timezone,
        ...defaultOptions
      })
    },

    getCurrentTimeForTimezone(timezone, options = {}) {
      if (!timezone) {
        return ''
      }

      const { format24Hour = false, includeSeconds = false } = options

      try {
        const now = new Date()

        // Format options for Intl.DateTimeFormat
        const formatOptions = {
          timeZone: timezone,
          hour: format24Hour ? '2-digit' : 'numeric',
          minute: '2-digit',
          ...(includeSeconds && { second: '2-digit' }),
          ...(format24Hour ? {} : { hour12: true })
        }

        const formatter = new Intl.DateTimeFormat('en-US', formatOptions)
        const parts = formatter.formatToParts(now)

        let hour = parts.find((p) => p.type === 'hour')?.value || '00'
        const minute = parts.find((p) => p.type === 'minute')?.value || '00'
        const second = parts.find((p) => p.type === 'second')?.value || '00'
        const dayPeriod = parts.find((p) => p.type === 'dayPeriod')?.value || ''

        // Pad hour for 12-hour format if needed
        if (!format24Hour && hour.length === 1) {
          hour = hour.padStart(2, '0')
        }

        if (format24Hour) {
          return includeSeconds ? `${hour}:${minute}:${second}` : `${hour}:${minute}`
        }
        const ampm = dayPeriod.toUpperCase()

        return includeSeconds ? `${hour}:${minute}:${second} ${ampm}` : `${hour}:${minute} ${ampm}`
      } catch (error) {
        console.error('Error formatting time for timezone:', timezone, error)

        return ''
      }
    },

    convertTo24Hour(timeString) {
      const [time, modifier] = timeString.split(' ')
      let [hours] = time.split(':')

      hours = parseInt(hours, 10)

      if (modifier === 'PM' && hours !== 12) {
        hours += 12
      }

      if (modifier === 'AM' && hours === 12) {
        hours = 0
      }

      return hours
    }
  },

  isDaytime(dateTime = new Date()) {
    const hour = dateTime.getHours()

    return hour >= 5 && hour < 19
  },
  renderMethods: {
    getWeatherConditionIcon(weatherCondition, dateTime = null) {
      // Default fallback to sunny

      const time = dateTime || new Date()
      const isDay = isDaytime(time)

      if (!weatherCondition || weatherCondition.trim() === '') {
        return isDay ? weatherSunny : clearNightIcon
      }

      // Normalize condition: lowercase, trim, remove spaces
      const normalized = weatherCondition.toLowerCase().trim().replace(/\s+/g, '')

      // Helper function to check if normalized string contains any of the keywords
      const containsAny = (str, keywords) => keywords.some((keyword) => str.includes(keyword))

      // Severe thunderstorm with lightning (WMO 95, 96, 99)
      if (containsAny(normalized, ['thunder', 'lightning'])) {
        return weatherThunder
      }

      // Storm (general storm without thunder/lightning)
      if (containsAny(normalized, ['storm'])) {
        return weatherStorm
      }

      // Hail (WMO 96, 99 when with thunder; else standalone hail)
      if (containsAny(normalized, ['hail'])) {
        return weatherHail
      }

      // Snow (WMO 71, 73, 75, 77, 85, 86) – snow_sunny for light/partly, else snow
      if (containsAny(normalized, ['snow', 'sleet'])) {
        if (containsAny(normalized, ['light', 'slight', 'partly', 'shower', 'sunny'])) {
          return weatherSnowSunny
        }

        return weatherSnow
      }

      // Drizzle (WMO 51, 53, 55, 56, 57)
      if (containsAny(normalized, ['drizzle'])) {
        return weatherDrizzle
      }

      // Rain / Showers (WMO 61–67, 80–82)
      if (containsAny(normalized, ['rain', 'shower'])) {
        // Partially cloudy + showers → partially_shower
        if (containsAny(normalized, ['shower', 'partly', 'slight', 'light', 'partial'])) {
          return weatherPartiallyShower
        }

        return weatherRainy
      }

      // Fog / Mist / Haze (WMO 45, 48)
      if (containsAny(normalized, ['fog', 'mist', 'haze'])) {
        return weatherFog
      }

      // Wind / High wind
      if (containsAny(normalized, ['wind', 'windy', 'gust'])) {
        return weatherWind
      }

      // Overcast (WMO 3)
      if (containsAny(normalized, ['overcast'])) {
        return weatherOvercast
      }
      // Clear / Sunny (WMO 0, 1)
      if (containsAny(normalized, ['clear', 'sunny'])) {
        return isDay ? weatherSunny : clearNightIcon
      }
      // Cloudy (WMO 2, 3)
      if (containsAny(normalized, ['cloud', 'mostly'])) {
        // Check for "partly cloudy" first
        if (containsAny(normalized, ['partly', 'partial'])) {
          return isDay ? weatherPartly : partlyNightIcon
        }

        return isDay ? weatherCloudy : partlyNightIcon
      }

      // Default fallback to weatherAnim (general weather animation)
      return weatherAnim
    },
    getCountryFlag(countryCode) {
      return `https://coffeeweb.s3.ap-south-1.amazonaws.com/Masters/Flags/${countryCode}.svg`
    },
    // urlHelpers function
    setUrlParam(currentSearch, typeValue) {
      const params = new URLSearchParams(currentSearch)

      params.set('type', typeValue)

      return params
    },
    isDaytime(dateTime = new Date()) {
      const hour = dateTime.getHours()

      return hour >= 5 && hour < 19
    },

    getWeatherLottieAsset(condition, dateTime = null) {
      const time = dateTime || new Date()
      const isDay = isDaytime(time)

      if (!condition || condition.trim() === '') {
        return isDay ? clearDay : clearNight
      }

      const normalized = condition.toLowerCase().trim().replace(/\s+/g, '')

      // Helper function to check if normalized string contains any of the keywords
      const containsAny = (str, keywords) => keywords.some((keyword) => str.includes(keyword))

      // Thunderstorm with lightning (WMO 95, 96, 99)
      if (containsAny(normalized, ['thunder', 'lightning', 'storm'])) {
        return isDay ? thunderstormDay : thunderstormNight
      }

      // Snow (WMO 71, 73, 75, 77, 85, 86)
      if (containsAny(normalized, ['snow', 'sleet'])) {
        return isDay ? snowDay : snowNight
      }

      // Drizzle (WMO 51, 53, 55, 56, 57)
      if (containsAny(normalized, ['drizzle'])) {
        return drizzle
      }

      // Rain / Showers (WMO 61–67, 80–82)
      if (containsAny(normalized, ['rain', 'shower'])) {
        // Heavy rain
        if (containsAny(normalized, ['heavy', 'moderate', 'intense'])) {
          return heavyRain
        }
        // Regular rain with day/night variants

        return isDay ? rainyDay : rainyNight
      }

      // Fog (WMO 45, 48)
      if (containsAny(normalized, ['fog', 'haze'])) {
        return fog
      }

      // Mist
      if (containsAny(normalized, ['mist'])) {
        return mist
      }

      // Wind / High wind
      if (containsAny(normalized, ['wind', 'windy', 'gust'])) {
        return wind
      }
      // Clear / Sunny (WMO 0, 1)
      if (containsAny(normalized, ['clear', 'sunny'])) {
        return isDay ? clearDay : clearNight
      }
      // Cloudy (WMO 2, 3)
      if (containsAny(normalized, ['cloud', 'mostly', 'overcast'])) {
        // Check for "partly cloudy" first
        if (containsAny(normalized, ['partly', 'partial'])) {
          return isDay ? partlyCloudyDay : partlyCloudyNight
        }
        // Fully cloudy/overcast (time-neutral)

        return cloudy
      }

      // Default fallback
      return isDay ? clearDay : clearNight
    }
  }
}
